/**
 * List of products which are exceeding the quantity allowed for selected delivery type
 * @attr {String} [quantity-label] - Quantity label
 * @attr {String} [quantity-label-short] - Quantity label short version
 * @attr {String} [css-class] - Additional css class for product item
 * @attr {String} [items-available-label] - Available amount of items label
 * @attr {Boolean} [use-adjusted-cart-entries] - Use adjusted cart array items as product entries
 *
 * @param {Array} entries - Product entries
 *
 * HTML Structure Example:
 * <e2-out-of-max-allowed-products></e2-out-of-max-allowed-products>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/cart';

export class OutOfMaxAllowedProducts extends LightLitElement {
    static get properties() {
        return {
            entriesWithLimitExceeds: { type: Array, attribute: false },
            entriesWithOutOfStock: { type: Array, attribute: false },
            isOutOfStock: { type: Boolean, attribute: false },
            cssClass: { type: String, attribute: 'css-class' },
            existingQuantityTitle: { type: String, attribute: 'existing-quantity-title' },
            maxAllowedQuantityTitle: { type: String, attribute: 'max-allowed-quantity-title' },
            priceTitle: { type: String, attribute: 'price-title' },
            itemsAvailableStockPartialLabel: {
                type: String,
                attribute: 'available-item-stock-partial-label',
            },
            itemsAvailableStockOosLabel: {
                type: String,
                attribute: 'available-item-stock-oos-label',
            },
            itemsAvailableDeliveryLimitationPartialLabel: {
                type: String,
                attribute: 'available-item-delivery-limitation-partial-label',
            },
            itemsAvailableDeliveryLimitationOosLabel: {
                type: String,
                attribute: 'available-item-delivery-limitation-oos-label',
            },
            maxAllowed: { type: Number, attribute: false },
        };
    }

    constructor() {
        super();

        this.entriesWithLimitExceeds = [];
        this.enabledFeatures = [];
        this.cssClass = '';
        this.quantityLabel = '';
        this.itemsAvailableLabel = '';
        this.quantityLabelShort = '';
    }

    getItem(name, content) {
        return html`
            <div class="product__${name}">${content}</div>
        `;
    }

    getVariantsClassName(isInline) {
        return classMap({
            product__variants: true,
            'product__variants--inline': isInline,
        });
    }

    getIconClassName({ stockLevel = 0 }) {
        const iconFlag = stockLevel === 0;
        return classMap({
            icon: true,
            'icon-circle': true,
            'product-list__product-status-icon': true,
            'product-list__product-status-icon--success': !iconFlag,
            'product-list__product-status-icon--error': iconFlag,
        });
    }

    getProductVariants(variants, isInline) {
        return html`
            <e2-product-variants
                class="${this.getVariantsClassName(isInline)}"
                .variants="${variants}"
                variant-class="product-variant--out-of-stock${isInline ? '-inline' : ''}"
                ?is-text-color="${isInline}"
            ></e2-product-variants>
        `;
    }

    updated(changedProperties) {
        if (changedProperties.has('entriesWithLimitExceeds')) {
            this.dispatchEvent(new CustomEvent('adjust-content-data'));
        }
    }

    getItemImage({ name = '', images }) {
        return html`
            <e2-product-image
                format="list"
                .images="${images}"
                .productName="${name}"
                classes="product-list-item__img"
            ></e2-product-image>
        `;
    }

    isOutOfStockOrMaxedLimit = entry => {
        const { quantity = 0, stockLevel = 0 } = entry;
        if (quantity > stockLevel) {
            return true;
        }
        return false;
    };

    getItemAvailableText(entry) {
        const { stockLevel = 0 } = entry;
        if (this.entriesWithOutOfStock.find(item => item.product.code === entry.product.code)) {
            return stockLevel <= 0
                ? this.itemsAvailableStockOosLabel.replace('{0}', stockLevel)
                : this.itemsAvailableStockPartialLabel.replace('{0}', stockLevel);
        }
        if (this.isOutOfStockOrMaxedLimit(entry)) {
            return stockLevel <= 0
                ? this.itemsAvailableDeliveryLimitationOosLabel.replace('{0}', stockLevel)
                : this.itemsAvailableDeliveryLimitationPartialLabel.replace('{0}', stockLevel);
        }

        return '';
    }

    getProductStatusTemplate(entry) {
        return html`
            <div class="product-list__product-status">
                <icon class="${this.getIconClassName(entry)}"></icon>
                <span class="product-list__product-status--value"
                    >${this.getItemAvailableText(entry)}</span
                >
            </div>
        `;
    }

    getProductDetails(entry) {
        const { name } = entry.product;
        return html`
            <div class="product-list__product-details">
                <div class="product-list__product-name">${name}</div>
                ${this.getProductStatusTemplate(entry, true)}
            </div>
        `;
    }

    renderRowTemplate(entry) {
        const { product = {}, quantity = 0, stockLevel = 0, price = {} } = entry;
        return html`
            <li class="product-list ${this.cssClass}">
                <div class="product-list__product-info">
                    ${this.getItemImage(product)} ${this.getProductDetails(entry)}
                </div>
                ${this.getItem(
                    'quantity-to-remove',
                    html`
                        <span class="product__quantity-value">
                            ${quantity}
                        </span>
                    `,
                )}
                ${this.getItem(
                    'quantity-to-remove',
                    html`
                        <span class="product__quantity-value">
                            ${stockLevel}
                        </span>
                    `,
                )}
                ${this.getItem('price', price.formattedValue || '')}
            </li>
        `;
    }

    getProductTemplateForLimitExceeds = ({ product = {}, quantity = 0, maxAllowed }) => {
        const { price = {}, variantValueCategories = [] } = product;
        const stockLevel = maxAllowed;

        return this.renderRowTemplate({
            product,
            stockLevel,
            quantity,
            price,
            variantValueCategories,
        });
    };

    getProductTemplateForOutOfStock = ({ product = {}, quantity = 0, stockData }) => {
        const { stock = {}, price = {}, variantValueCategories = [] } = product;
        const stockLevel = stockData ? stockData.stockLevel : stock.stockLevel;

        return this.renderRowTemplate({
            product,
            stockLevel,
            quantity,
            price,
            variantValueCategories,
        });
    };

    adjustEntriesForRendering = () => {
        const commonEntries = [];
        let limitArray = [...this.entriesWithLimitExceeds];
        let outOfStockArray = [...this.entriesWithOutOfStock];
        if (this.maxAllowed && this.maxAllowed > 0) {
            // Remove common products from both arrays and push them into commonEntries
            limitArray = limitArray.filter(entry => {
                const commonEntryIndex = outOfStockArray.findIndex(
                    outOfStockEntry => outOfStockEntry.product.code === entry.product.code,
                );
                if (commonEntryIndex !== -1) {
                    commonEntries.push(entry);
                    outOfStockArray.splice(commonEntryIndex, 1);
                    return false;
                }
                return true;
            });

            commonEntries.forEach(entry => {
                const { product = {}, quantity = 0, stockData } = entry;
                const maxAllowed = this.maxAllowed;
                const { stock = {} } = product;
                const stockLevel = stockData ? stockData.stockLevel : stock.stockLevel;
                const comparisonValueStock = quantity - stockLevel;
                const comparisonValue = quantity - maxAllowed;
                if (comparisonValue >= comparisonValueStock) {
                    limitArray.push({ ...entry });
                } else {
                    outOfStockArray.push({ ...entry });
                }
            });
        }

        return {
            limitArray,
            outOfStockArray,
        };
    };

    renderTitleRow() {
        return html`
            <li class="product__title">
                <span class="product__title--heading-spacer"></span>
                <span class="product__title--heading-label">${this.existingQuantityTitle}</span>
                <span class="product__title--heading-label">${this.maxAllowedQuantityTitle}</span>
                <span class="product__title--heading-label">${this.priceTitle}</span>
            </li>
        `;
    }

    render() {
        const { limitArray, outOfStockArray } = this.adjustEntriesForRendering();
        return html`
            <ul class="product-list">
                ${this.renderTitleRow()} ${limitArray.map(this.getProductTemplateForLimitExceeds)}
                ${this.isOutOfStock
                    ? outOfStockArray.map(this.getProductTemplateForOutOfStock)
                    : ''}
            </ul>
        `;
    }
}

const mapStateToProps = state => {
    const entriesWithLimitExceeds = selectors.entriesWithHigherQuantityThanAllowed(state);
    const entriesWithOutOfStock = selectors.getOutOfStockEntries(state);
    const maxAllowed = selectors.maxAllowedProductQuantity(state);
    const haveOutOfStock = selectors.getHaveOutOfStock(state);
    const productEntriesDTO = entries =>
        entries.map(({ quantity, product, entry, ...rest }) => {
            if (!product && entry) {
                product = entry.product;
            }
            return { quantity, product, maxAllowed, ...rest };
        });

    return {
        entriesWithLimitExceeds: productEntriesDTO(entriesWithLimitExceeds),
        entriesWithOutOfStock: productEntriesDTO(entriesWithOutOfStock),
        isOutOfStock: haveOutOfStock,
        maxAllowed,
    };
};

export default () =>
    customElements.define(
        'e2-out-of-max-allowed-products',
        connect(mapStateToProps)(OutOfMaxAllowedProducts),
    );
